import React from 'react';
import { Sensor } from '../services/api';

interface RangeValue {
  normalMin: number;
  normalMax: number;
}

interface RangeSettings {
  power_sensor: {
    temperature: RangeValue;
    humidity: RangeValue;
    battery: RangeValue;
  };
  water_sensor: {
    water_level: RangeValue;
  };
  smoke_sensor: {
    smoke: RangeValue;
  };
  rgb_camera: {
    motion: RangeValue;
    light: RangeValue;
  };
}

interface ThresholdSettingsProps {
  selectedSensor: Sensor;
  rangeSettings: RangeSettings;
  handleRangeChange: (sensorType: string, param: string, newValue: RangeValue) => void;
  handleSaveThreshold: () => void;
  setShowThresholdSettings: (show: boolean) => void;
  RangeSlider: React.FC<{
    min: number;
    max: number;
    value: RangeValue;
    onChange: (value: RangeValue) => void;
    label: string;
    unit: string;
    currentValue: number;
  }>;
}

const ThresholdSettings: React.FC<ThresholdSettingsProps> = ({
  selectedSensor,
  rangeSettings,
  handleRangeChange,
  handleSaveThreshold,
  setShowThresholdSettings,
  RangeSlider,
}) => {
  return (
    <div className="space-y-6 p-4">
      {/* 根據感測器類型顯示不同的標題 */}
      <div className="text-lg font-semibold text-gray-800 mb-4">
        {selectedSensor?.type === '電量感測器' && '⚡ 電量感測器閥值設定'}
        {selectedSensor?.type === '水位感測器' && '💧 水位感測器閥值設定'}
        {selectedSensor?.type === '煙霧感測器' && '🌫️ 煙霧感測器閥值設定'}
        {selectedSensor?.type === 'RGB攝影機' && '📷 RGB攝影機閥值設定'}
      </div>

      {/* 電量感測器設定 */}
      {selectedSensor?.type === '電量感測器' && (
        <div className="space-y-6">
          <RangeSlider min={-10} max={60} value={rangeSettings.power_sensor.temperature}
            onChange={(value) => handleRangeChange('power_sensor', 'temperature', value)}
            label="溫度" unit="°C" currentValue={selectedSensor?.temperature || 0} />
          <RangeSlider min={0} max={5} value={rangeSettings.power_sensor.battery}
            onChange={(value) => handleRangeChange('power_sensor', 'battery', value)}
            label="電壓" unit="V" currentValue={selectedSensor?.battery || 0} />
        </div>
      )}

      {/* 水位感測器設定 */}
      {selectedSensor?.type === '水位感測器' && (
        <div className="space-y-6">
          <RangeSlider min={0} max={10} value={rangeSettings.water_sensor.water_level}
            onChange={(value) => handleRangeChange('water_sensor', 'water_level', value)}
            label="水位" unit="m" currentValue={selectedSensor?.smoke_level || 0} />
        </div>
      )}

      {/* 煙霧感測器設定 */}
      {selectedSensor?.type === '煙霧感測器' && (
        <div className="space-y-6">
          <RangeSlider min={0} max={200} value={rangeSettings.smoke_sensor.smoke}
            onChange={(value) => handleRangeChange('smoke_sensor', 'smoke', value)}
            label="煙霧濃度" unit="ppm" currentValue={selectedSensor?.smoke_level || 0} />
        </div>
      )}

      {/* RGB攝影機設定 */}
      {selectedSensor?.type === 'RGB攝影機' && (
        <div className="space-y-6">
          <RangeSlider
            min={0}
            max={20}
            value={rangeSettings.rgb_camera.motion}
            onChange={(value) => handleRangeChange('rgb_camera', 'motion', value)}
            label="移動偵測"
            unit="次/分"
            currentValue={8}
          />

          <RangeSlider
            min={0}
            max={500}
            value={rangeSettings.rgb_camera.light}
            onChange={(value) => handleRangeChange('rgb_camera', 'light', value)}
            label="光線強度"
            unit="lux"
            currentValue={120}
          />
        </div>
      )}

      <div className="flex gap-2 pt-4 border-t border-gray-200">
        <button
          onClick={handleSaveThreshold}
          className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm font-medium"
        >
          儲存設定
        </button>
        <button
          onClick={() => setShowThresholdSettings(false)}
          className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded text-sm font-medium"
        >
          返回
        </button>
      </div>
    </div>
  );
};

export default ThresholdSettings;
